/**
 * Area Role - Convert Pillar
 *
 * Conversão entre unidades de área usando arquitetura hub-and-spoke.
 * Base: square_meter (m²)
 *
 * @example
 * import { convertArea, toBaseArea, fromBaseArea } from '@attrx/role-morphic/area/convert';
 *
 * convertArea('hectare', 'acre', 1);     // 2.47105...
 * toBaseArea('hectare', 1);              // 10000
 * fromBaseArea('acre', 10000);           // 2.47105...
 */

import { AREA_UNITS, type AreaUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

// =============================================================================
// CONSTANTS
// =============================================================================

/** Variante base para conversões */
export const AREA_BASE: AreaUnit = "square_meter";

// =============================================================================
// CONVERT
// =============================================================================

/**
 * Converte valor para a unidade base (square_meter)
 *
 * @param variant - Unidade de origem
 * @param value - Valor a converter
 * @returns Valor em square_meter
 *
 * @example
 * toBaseArea('hectare', 1);     // 10000
 * toBaseArea('acre', 1);        // 4046.8564224
 */
export function toBaseArea(variant: AreaUnit, value: number): number {
  const unit = AREA_UNITS[variant];

  if (!unit) {
    throw new Error(`Unknown area variant: ${variant}`);
  }

  return value * (unit.factor ?? 1);
}

/**
 * Converte valor da unidade base (square_meter) para outra unidade
 *
 * @param variant - Unidade de destino
 * @param baseValue - Valor em square_meter
 * @returns Valor na unidade de destino
 *
 * @example
 * fromBaseArea('hectare', 10000);  // 1
 * fromBaseArea('acre', 4046.86);   // ~1
 */
export function fromBaseArea(variant: AreaUnit, baseValue: number): number {
  const unit = AREA_UNITS[variant];

  if (!unit) {
    throw new Error(`Unknown area variant: ${variant}`);
  }

  return baseValue / (unit.factor ?? 1);
}

/**
 * Converte entre duas unidades de área
 *
 * @param from - Unidade de origem
 * @param to - Unidade de destino
 * @param value - Valor a converter
 * @returns Valor convertido
 *
 * @example
 * convertArea('hectare', 'acre', 1);           // 2.47105...
 * convertArea('square_meter', 'hectare', 10000); // 1
 */
export function convertArea(
  from: AreaUnit,
  to: AreaUnit,
  value: number
): number {
  // Otimização: mesma unidade
  if (from === to) {
    return value;
  }

  // Hub-and-spoke: from → base → to
  const baseValue = toBaseArea(from, value);
  return fromBaseArea(to, baseValue);
}

/**
 * Versão safe de convertArea que retorna Result
 *
 * @example
 * tryConvertArea('hectare', 'acre', 1);
 * // { ok: true, value: 2.47105... }
 *
 * tryConvertArea('invalid', 'acre', 1);
 * // { ok: false, error: 'Unknown area variant: invalid' }
 */
export function tryConvertArea(
  from: AreaUnit,
  to: AreaUnit,
  value: number
): Result<number> {
  try {
    const result = convertArea(from, to, value);
    return { ok: true, value: result };
  } catch (err) {
    return { ok: false, error: String(err) };
  }
}

/**
 * Verifica se uma variante existe
 */
export function hasAreaVariant(variant: string): variant is AreaUnit {
  return variant in AREA_UNITS;
}

/**
 * Lista todas as variantes disponíveis
 */
export function getAreaVariants(): AreaUnit[] {
  return Object.keys(AREA_UNITS) as AreaUnit[];
}
